// JSON toolkit: format/validate/minify plus JSON<->YAML and JSON<->CSV.
// Pure functions; conversions throw with a clear message on bad input (Fail Fast).
import yaml from 'js-yaml';
import Papa from 'papaparse';
import { parse as parseToml, stringify as stringifyToml } from 'smol-toml';

export type Mode =
  | 'format'
  | 'minify'
  | 'json2yaml'
  | 'yaml2json'
  | 'json2csv'
  | 'csv2json'
  | 'json2toml'
  | 'toml2json';

export function formatJson(input: string): string {
  return JSON.stringify(JSON.parse(input), null, 2);
}

export function minifyJson(input: string): string {
  return JSON.stringify(JSON.parse(input));
}

export function jsonToYaml(input: string): string {
  return yaml.dump(JSON.parse(input));
}

export function yamlToJson(input: string): string {
  return JSON.stringify(yaml.load(input), null, 2);
}

export function jsonToCsv(input: string): string {
  const data = JSON.parse(input);
  if (!Array.isArray(data)) {
    throw new Error('CSV export needs a JSON array of objects');
  }
  return Papa.unparse(data, { newline: '\n' });
}

export function csvToJson(input: string): string {
  const result = Papa.parse(input.trim(), { header: true, skipEmptyLines: true });
  if (result.errors.length > 0) {
    throw new Error(result.errors[0].message);
  }
  return JSON.stringify(result.data, null, 2);
}

export function jsonToToml(input: string): string {
  const data = JSON.parse(input);
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    throw new Error('TOML export needs a JSON object at the top level');
  }
  return stringifyToml(data);
}

export function tomlToJson(input: string): string {
  return JSON.stringify(parseToml(input), null, 2);
}

const RUNNERS: Record<Mode, (input: string) => string> = {
  format: formatJson,
  minify: minifyJson,
  json2yaml: jsonToYaml,
  yaml2json: yamlToJson,
  json2csv: jsonToCsv,
  csv2json: csvToJson,
  json2toml: jsonToToml,
  toml2json: tomlToJson,
};

export interface ConvertResult {
  output: string;
  error: string | null;
}

export function convert(mode: Mode, input: string): ConvertResult {
  if (!input.trim()) return { output: '', error: null };
  try {
    return { output: RUNNERS[mode](input), error: null };
  } catch (e) {
    return { output: '', error: (e as Error).message };
  }
}

/** Output language for syntax highlighting per mode. */
export function outputLang(mode: Mode): string {
  if (mode === 'json2yaml') return 'yaml';
  if (mode === 'json2toml') return 'toml';
  if (mode === 'json2csv') return 'text';
  return 'json';
}
